import { ref } from 'vue';
import JsSIP from 'jssip';
import SipCredentialsAPI from 'dashboard/api/sipCredentials';
import SoftphoneAPI from 'dashboard/api/softphone';

// Singleton SIP user-agent shared across the whole dashboard. JsSIP holds
// non-serializable objects (UA/RTCSession), so state lives in module-level refs
// instead of Vuex.
const registration = ref('idle'); // idle | unconfigured | connecting | registered | failed
const call = ref(null); // null | { direction: 'in'|'out', state: 'ringing'|'active', number }
const isMuted = ref(false);
const durationSeconds = ref(0);
const lastError = ref('');
const isOnHold = ref(false);
const isTransferring = ref(false);
// Who is on the other side: KALL-AI contact and IXC client ({ display, contact, ixc }).
const callerInfo = ref(null);
// { level: 'good' | 'unstable' | 'bad', rttMs, lossPct, jitterMs } while a call is active.
const quality = ref(null);

let ua = null;
let session = null;
let durationTimer = null;
let audioEl = null;
let config = null;
let answering = false; // true while session.answer() is in flight — blocks double-clicks
// Ramais are managed by admins (Settings → Softphone) and the box picks changes
// up every 20s. A refused register after a password rotation, or an agent whose
// ramal is created while the dashboard is open, recover by fetching the config
// again once the box has had time to sync.
const BOX_SYNC_WAIT_MS = 25000;
const UNCONFIGURED_RETRY_MS = 60000;
let refetchedAfterAuthFailure = false;
let retryTimer = null;
// A dropped network (Wi-Fi switch, 4G) leaves the phone unregistered: retry on
// our own instead of waiting for the agent to notice and click Reconectar.
const NETWORK_RETRY_MS = 30000;

// Call quality from the browser's own WebRTC stats, sampled while talking.
const QUALITY_MS = 2000;
let qualityTimer = null;
let lastPackets = null;

const qualityLevel = (rtt, jitter, lossPct) => {
  if (lossPct >= 5 || rtt > 0.4 || jitter > 0.06) return 'bad';
  if (lossPct >= 2 || rtt > 0.25 || jitter > 0.03) return 'unstable';
  return 'good';
};

const scheduleRetry = (delay, action) => {
  clearTimeout(retryTimer);
  // eslint-disable-next-line no-use-before-define
  retryTimer = setTimeout(() => useSoftphone()[action](), delay);
};

// STUN lets the browser discover its public address so ICE can pick a path the
// PBX can actually reach (host-only candidates behind NAT are unreachable).
const PC_CONFIG = {
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
};

// ICE servers come from the backend (so STUN/TURN can be configured per-install
// via ENV without a frontend deploy); fall back to a public STUN.
const pcConfig = () =>
  Array.isArray(config?.ice_servers)
    ? { iceServers: config.ice_servers }
    : PC_CONFIG;

// Incoming-call ring + desktop notification so an agent doesn't miss a call when
// the dashboard tab is in the background or the sidebar isn't focused.
let ringCtx = null;
let ringInterval = null;
let incomingNotification = null;

const startRing = label => {
  try {
    if ('Notification' in window) {
      if (Notification.permission === 'granted') {
        incomingNotification = new Notification('Chamada recebida', {
          body: label || '',
          tag: 'softphone-incoming',
        });
      } else if (Notification.permission === 'default') {
        Notification.requestPermission();
      }
    }
  } catch (error) {
    /* noop */
  }
  try {
    if (!ringCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) ringCtx = new Ctx();
    }
    if (!ringCtx) return;
    if (ringCtx.state === 'suspended') ringCtx.resume();
    const beep = () => {
      const osc = ringCtx.createOscillator();
      const gain = ringCtx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 480;
      gain.gain.value = 0.07;
      osc.connect(gain);
      gain.connect(ringCtx.destination);
      osc.start();
      osc.stop(ringCtx.currentTime + 0.7);
    };
    beep();
    ringInterval = setInterval(beep, 2400);
  } catch (error) {
    /* noop */
  }
};

const stopRing = () => {
  if (ringInterval) clearInterval(ringInterval);
  ringInterval = null;
  if (incomingNotification) {
    try {
      incomingNotification.close();
    } catch (error) {
      /* noop */
    }
    incomingNotification = null;
  }
};

const ensureAudio = () => {
  if (!audioEl) {
    audioEl = document.createElement('audio');
    audioEl.autoplay = true;
    audioEl.setAttribute('data-softphone-audio', 'true');
    document.body.appendChild(audioEl);
  }
  return audioEl;
};

const stopTimer = () => {
  if (durationTimer) clearInterval(durationTimer);
  durationTimer = null;
};

const startTimer = () => {
  stopTimer();
  durationSeconds.value = 0;
  durationTimer = setInterval(() => {
    durationSeconds.value += 1;
  }, 1000);
};

const stopQuality = () => {
  clearInterval(qualityTimer);
  qualityTimer = null;
  lastPackets = null;
  quality.value = null;
};

const sampleQuality = async () => {
  const pc = session?.connection;
  if (!pc) return;
  let rtt = 0;
  let jitter = 0;
  let lost = 0;
  let received = 0;
  (await pc.getStats()).forEach(report => {
    if (report.kind !== 'audio') return;
    if (report.type === 'remote-inbound-rtp' && report.roundTripTime != null) {
      rtt = report.roundTripTime;
    }
    if (report.type === 'inbound-rtp') {
      jitter = report.jitter || 0;
      lost = report.packetsLost || 0;
      received = report.packetsReceived || 0;
    }
  });
  const newLost = lastPackets ? lost - lastPackets.lost : 0;
  const newReceived = lastPackets ? received - lastPackets.received : 0;
  lastPackets = { lost, received };
  const total = newLost + newReceived;
  const lossPct = total > 0 ? (newLost / total) * 100 : 0;
  quality.value = {
    level: qualityLevel(rtt, jitter, lossPct),
    rttMs: Math.round(rtt * 1000),
    lossPct: Math.round(lossPct),
    jitterMs: Math.round(jitter * 1000),
  };
};

const startQuality = () => {
  if (qualityTimer) return;
  qualityTimer = setInterval(() => {
    sampleQuality().catch(() => {});
  }, QUALITY_MS);
};

// Only real phone numbers are looked up; a short number is a colleague's ramal.
const lookupCaller = async number => {
  callerInfo.value = null;
  if (number.replace(/\D/g, '').length < 8) return;
  try {
    const { data } = await SoftphoneAPI.caller(number);
    if (call.value?.number === number) callerInfo.value = data;
  } catch (error) {
    /* the call goes on without the card */
  }
};

const resetCall = () => {
  stopTimer();
  stopRing();
  stopQuality();
  if (audioEl) audioEl.srcObject = null;
  session = null;
  call.value = null;
  isMuted.value = false;
  isOnHold.value = false;
  isTransferring.value = false;
  callerInfo.value = null;
  durationSeconds.value = 0;
  answering = false;
};

const markActive = () => {
  stopRing();
  answering = false;
  if (call.value) call.value = { ...call.value, state: 'active' };
  if (!durationTimer) startTimer();
  startQuality();
};

const attachMedia = sipSession => {
  const wire = pc => {
    pc.addEventListener('track', event => {
      if (event.streams && event.streams[0]) {
        ensureAudio().srcObject = event.streams[0];
      }
    });
  };
  if (sipSession.connection) wire(sipSession.connection);
  sipSession.on('peerconnection', data => wire(data.peerconnection));
};

const bindSession = sipSession => {
  session = sipSession;
  attachMedia(sipSession);
  sipSession.on('accepted', markActive);
  sipSession.on('confirmed', markActive);
  sipSession.on('hold', () => {
    isOnHold.value = true;
  });
  sipSession.on('unhold', () => {
    isOnHold.value = false;
  });
  sipSession.on('ended', resetCall);
  sipSession.on('failed', data => {
    lastError.value = data?.cause
      ? `chamada falhou: ${data.cause}`
      : 'chamada falhou';
    resetCall();
  });
};

const bindUa = () => {
  ua.on('connecting', () => {
    registration.value = 'connecting';
  });
  ua.on('registered', () => {
    registration.value = 'registered';
    lastError.value = '';
    refetchedAfterAuthFailure = false;
  });
  ua.on('unregistered', () => {
    registration.value = 'idle';
  });
  ua.on('registrationFailed', data => {
    registration.value = 'failed';
    lastError.value =
      (data?.response &&
        `SIP ${data.response.status_code} ${data.response.reason_phrase || ''}`.trim()) ||
      data?.cause ||
      'registro recusado';
    if (data?.cause !== JsSIP.C.causes.AUTHENTICATION_ERROR) {
      scheduleRetry(NETWORK_RETRY_MS, 'reconnect');
    } else if (!refetchedAfterAuthFailure && !session) {
      refetchedAfterAuthFailure = true;
      scheduleRetry(BOX_SYNC_WAIT_MS, 'reconnect');
    }
  });
  ua.on('disconnected', data => {
    if (registration.value !== 'registered') {
      lastError.value =
        data?.reason ||
        (data?.code ? `WebSocket fechou (${data.code})` : lastError.value);
    }
  });
  ua.on('newRTCSession', ({ session: sipSession, originator }) => {
    // Single-line softphone: reject a second concurrent call.
    if (session) {
      sipSession.terminate({ status_code: 486 });
      return;
    }
    const number = sipSession.remote_identity?.uri?.user || '';
    call.value = {
      direction: originator === 'remote' ? 'in' : 'out',
      state: 'ringing',
      number,
    };
    if (originator === 'remote') startRing(number);
    bindSession(sipSession);
    lookupCaller(number);
  });
};

// Best-effort teardown on tab close/refresh so a mid-call leg doesn't linger on
// the PBX (zombie channel that bills until RTP timeout) and the registration is
// released instead of going stale.
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    if (!ua || session || registration.value === 'registered') return;
    // eslint-disable-next-line no-use-before-define
    useSoftphone().reconnect();
  });
  window.addEventListener('beforeunload', () => {
    try {
      if (session) session.terminate();
    } catch (error) {
      /* noop */
    }
    try {
      if (ua) ua.stop();
    } catch (error) {
      /* noop */
    }
  });
}

export function useSoftphone() {
  const connect = async () => {
    if (ua) return;
    // An agent without a ramal keeps the phone hidden while we poll for one.
    if (registration.value !== 'unconfigured')
      registration.value = 'connecting';
    try {
      const { data } = await SipCredentialsAPI.connection();
      if (!data.configured) {
        registration.value = 'unconfigured';
        scheduleRetry(UNCONFIGURED_RETRY_MS, 'connect');
        return;
      }
      registration.value = 'connecting';
      // Ask once up front so the desktop notification can fire on the first call.
      try {
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission();
        }
      } catch (error) {
        /* noop */
      }
      config = data;
      const socket = new JsSIP.WebSocketInterface(data.ws_uri);
      ua = new JsSIP.UA({
        sockets: [socket],
        uri: data.sip_uri,
        password: data.password,
        register: true,
        register_expires: 120,
        session_timers: false,
      });
      bindUa();
      ua.start();
    } catch (error) {
      registration.value = 'failed';
      lastError.value = 'falha ao iniciar (credenciais/rede)';
      scheduleRetry(NETWORK_RETRY_MS, 'reconnect');
    }
  };

  const disconnect = () => {
    clearTimeout(retryTimer);
    if (session) {
      try {
        session.terminate();
      } catch (error) {
        /* noop */
      }
    }
    if (ua) {
      try {
        ua.stop();
      } catch (error) {
        /* noop */
      }
      ua = null;
    }
    resetCall();
    registration.value = 'idle';
  };

  // Force a full teardown + rebuild. The recovery UI must call THIS (not connect),
  // because connect() early-returns when `ua` still exists after a failed register.
  const reconnect = async () => {
    disconnect();
    await connect();
  };

  const startCall = number => {
    if (!ua) {
      lastError.value = 'softphone não conectado — clique em Reconectar';
      return;
    }
    if (session) {
      lastError.value = 'linha ocupada — encerre a chamada atual primeiro';
      return;
    }
    if (!number) return;
    lastError.value = '';
    try {
      ua.call(`sip:${number}@${config.domain}`, {
        mediaConstraints: { audio: true, video: false },
        pcConfig: pcConfig(),
      });
    } catch (error) {
      lastError.value = `falha ao ligar: ${error?.message || error}`;
      resetCall();
    }
  };

  const answer = () => {
    if (!session || call.value?.direction !== 'in' || answering) return;
    answering = true;
    stopRing();
    lastError.value = '';
    // Optimistic: flip the UI off the "ringing" state right away so the agent
    // doesn't keep clicking while ICE gathers (which threw "Invalid status: 5").
    call.value = { ...call.value, state: 'active' };
    try {
      session.answer({
        mediaConstraints: { audio: true, video: false },
        pcConfig: pcConfig(),
      });
    } catch (error) {
      answering = false;
      lastError.value = `falha ao atender: ${error?.message || error}`;
      resetCall();
    }
  };

  const hangup = () => {
    // Always clear the line, even if terminate throws or the session is wedged —
    // a stuck session silently blocks every future call (startCall guard).
    try {
      if (session) session.terminate();
    } catch (error) {
      /* noop */
    }
    resetCall();
  };

  const toggleMute = () => {
    if (!session) return;
    if (isMuted.value) session.unmute({ audio: true });
    else session.mute({ audio: true });
    isMuted.value = !isMuted.value;
  };

  const toggleHold = () => {
    if (!session || call.value?.state !== 'active') return;
    if (isOnHold.value) session.unhold();
    else session.hold();
  };

  // Blind transfer (SIP REFER): the box sends the other party to the target and
  // then hangs up this leg, which ends the call here.
  const transfer = target => {
    if (!session || !target || isTransferring.value) return;
    isTransferring.value = true;
    lastError.value = '';
    session.refer(`sip:${target}@${config.domain}`, {
      eventHandlers: {
        failed: () => {
          isTransferring.value = false;
          lastError.value = 'transferência recusada';
        },
      },
    });
  };

  const sendDtmf = tone => {
    if (session && call.value?.state === 'active') session.sendDTMF(tone);
  };

  return {
    registration,
    call,
    isMuted,
    durationSeconds,
    lastError,
    connect,
    disconnect,
    reconnect,
    startCall,
    answer,
    hangup,
    toggleMute,
    toggleHold,
    transfer,
    sendDtmf,
    isOnHold,
    isTransferring,
    callerInfo,
    quality,
  };
}
